/**
 * Icon Component - Wrapper for Heroicons
 *
 * @description Provides a consistent interface for using Heroicons across the application.
 * Supports both outline (24px) and solid (20px) variants.
 *
 * @example
 * // Using named icon
 * <Icon name="document" variant="outline" className="h-5 w-5" />
 *
 * // Using direct Heroicon import
 * import { DocumentIcon } from '@heroicons/react/24/outline';
 * <DocumentIcon className="h-5 w-5 text-mv-blue-600" />
 */

import * as OutlineIcons from '@heroicons/react/24/outline';
import * as SolidIcons from '@heroicons/react/24/solid';
import { type ComponentProps } from 'react';

// Map of common icon names to Heroicon components
const iconMap = {
  // Navigation & Actions
  home: { outline: OutlineIcons.HomeIcon, solid: SolidIcons.HomeIcon },
  search: { outline: OutlineIcons.MagnifyingGlassIcon, solid: SolidIcons.MagnifyingGlassIcon },
  menu: { outline: OutlineIcons.Bars3Icon, solid: SolidIcons.Bars3Icon },
  close: { outline: OutlineIcons.XMarkIcon, solid: SolidIcons.XMarkIcon },
  plus: { outline: OutlineIcons.PlusIcon, solid: SolidIcons.PlusIcon },
  minus: { outline: OutlineIcons.MinusIcon, solid: SolidIcons.MinusIcon },
  check: { outline: OutlineIcons.CheckIcon, solid: SolidIcons.CheckIcon },
  chevronDown: { outline: OutlineIcons.ChevronDownIcon, solid: SolidIcons.ChevronDownIcon },
  chevronUp: { outline: OutlineIcons.ChevronUpIcon, solid: SolidIcons.ChevronUpIcon },
  chevronLeft: { outline: OutlineIcons.ChevronLeftIcon, solid: SolidIcons.ChevronLeftIcon },
  chevronRight: { outline: OutlineIcons.ChevronRightIcon, solid: SolidIcons.ChevronRightIcon },
  arrowLeft: { outline: OutlineIcons.ArrowLeftIcon, solid: SolidIcons.ArrowLeftIcon },
  arrowRight: { outline: OutlineIcons.ArrowRightIcon, solid: SolidIcons.ArrowRightIcon },

  // Documents & Files
  document: { outline: OutlineIcons.DocumentIcon, solid: SolidIcons.DocumentIcon },
  documentText: { outline: OutlineIcons.DocumentTextIcon, solid: SolidIcons.DocumentTextIcon },
  folder: { outline: OutlineIcons.FolderIcon, solid: SolidIcons.FolderIcon },
  folderOpen: { outline: OutlineIcons.FolderOpenIcon, solid: SolidIcons.FolderOpenIcon },
  paperClip: { outline: OutlineIcons.PaperClipIcon, solid: SolidIcons.PaperClipIcon },
  cloudArrowUp: { outline: OutlineIcons.CloudArrowUpIcon, solid: SolidIcons.CloudArrowUpIcon },
  cloudArrowDown: { outline: OutlineIcons.CloudArrowDownIcon, solid: SolidIcons.CloudArrowDownIcon },
  arrowDownTray: { outline: OutlineIcons.ArrowDownTrayIcon, solid: SolidIcons.ArrowDownTrayIcon },
  arrowUpTray: { outline: OutlineIcons.ArrowUpTrayIcon, solid: SolidIcons.ArrowUpTrayIcon },

  // Users & Auth
  user: { outline: OutlineIcons.UserIcon, solid: SolidIcons.UserIcon },
  userGroup: { outline: OutlineIcons.UserGroupIcon, solid: SolidIcons.UserGroupIcon },
  userPlus: { outline: OutlineIcons.UserPlusIcon, solid: SolidIcons.UserPlusIcon },
  userCircle: { outline: OutlineIcons.UserCircleIcon, solid: SolidIcons.UserCircleIcon },
  key: { outline: OutlineIcons.KeyIcon, solid: SolidIcons.KeyIcon },
  lock: { outline: OutlineIcons.LockClosedIcon, solid: SolidIcons.LockClosedIcon },
  lockClosed: { outline: OutlineIcons.LockClosedIcon, solid: SolidIcons.LockClosedIcon },
  lockOpen: { outline: OutlineIcons.LockOpenIcon, solid: SolidIcons.LockOpenIcon },
  fingerPrint: { outline: OutlineIcons.FingerPrintIcon, solid: SolidIcons.FingerPrintIcon },

  // Status & Feedback
  checkCircle: { outline: OutlineIcons.CheckCircleIcon, solid: SolidIcons.CheckCircleIcon },
  exclamationCircle: { outline: OutlineIcons.ExclamationCircleIcon, solid: SolidIcons.ExclamationCircleIcon },
  exclamationTriangle: { outline: OutlineIcons.ExclamationTriangleIcon, solid: SolidIcons.ExclamationTriangleIcon },
  informationCircle: { outline: OutlineIcons.InformationCircleIcon, solid: SolidIcons.InformationCircleIcon },
  xCircle: { outline: OutlineIcons.XCircleIcon, solid: SolidIcons.XCircleIcon },
  questionMarkCircle: { outline: OutlineIcons.QuestionMarkCircleIcon, solid: SolidIcons.QuestionMarkCircleIcon },

  // Security & Shield
  shield: { outline: OutlineIcons.ShieldCheckIcon, solid: SolidIcons.ShieldCheckIcon },
  shieldCheck: { outline: OutlineIcons.ShieldCheckIcon, solid: SolidIcons.ShieldCheckIcon },
  shieldExclamation: { outline: OutlineIcons.ShieldExclamationIcon, solid: SolidIcons.ShieldExclamationIcon },

  // Communication
  bell: { outline: OutlineIcons.BellIcon, solid: SolidIcons.BellIcon },
  bellAlert: { outline: OutlineIcons.BellAlertIcon, solid: SolidIcons.BellAlertIcon },
  envelope: { outline: OutlineIcons.EnvelopeIcon, solid: SolidIcons.EnvelopeIcon },
  chatBubble: { outline: OutlineIcons.ChatBubbleLeftIcon, solid: SolidIcons.ChatBubbleLeftIcon },
  phone: { outline: OutlineIcons.PhoneIcon, solid: SolidIcons.PhoneIcon },

  // Health
  heart: { outline: OutlineIcons.HeartIcon, solid: SolidIcons.HeartIcon },

  // Finance & Billing
  creditCard: { outline: OutlineIcons.CreditCardIcon, solid: SolidIcons.CreditCardIcon },
  banknotes: { outline: OutlineIcons.BanknotesIcon, solid: SolidIcons.BanknotesIcon },
  currencyEuro: { outline: OutlineIcons.CurrencyEuroIcon, solid: SolidIcons.CurrencyEuroIcon },
  receipt: { outline: OutlineIcons.ReceiptPercentIcon, solid: SolidIcons.ReceiptPercentIcon },

  // Settings & Tools
  cog: { outline: OutlineIcons.Cog6ToothIcon, solid: SolidIcons.Cog6ToothIcon },
  cog6Tooth: { outline: OutlineIcons.Cog6ToothIcon, solid: SolidIcons.Cog6ToothIcon },
  wrench: { outline: OutlineIcons.WrenchIcon, solid: SolidIcons.WrenchIcon },
  adjustments: { outline: OutlineIcons.AdjustmentsHorizontalIcon, solid: SolidIcons.AdjustmentsHorizontalIcon },
  pencil: { outline: OutlineIcons.PencilIcon, solid: SolidIcons.PencilIcon },
  trash: { outline: OutlineIcons.TrashIcon, solid: SolidIcons.TrashIcon },

  // Data & Charts
  chartBar: { outline: OutlineIcons.ChartBarIcon, solid: SolidIcons.ChartBarIcon },
  chartPie: { outline: OutlineIcons.ChartPieIcon, solid: SolidIcons.ChartPieIcon },
  presentationChart: { outline: OutlineIcons.PresentationChartLineIcon, solid: SolidIcons.PresentationChartLineIcon },
  tableCells: { outline: OutlineIcons.TableCellsIcon, solid: SolidIcons.TableCellsIcon },

  // Time & Calendar
  calendar: { outline: OutlineIcons.CalendarIcon, solid: SolidIcons.CalendarIcon },
  calendarDays: { outline: OutlineIcons.CalendarDaysIcon, solid: SolidIcons.CalendarDaysIcon },
  clock: { outline: OutlineIcons.ClockIcon, solid: SolidIcons.ClockIcon },

  // AI & Automation
  sparkles: { outline: OutlineIcons.SparklesIcon, solid: SolidIcons.SparklesIcon },
  lightBulb: { outline: OutlineIcons.LightBulbIcon, solid: SolidIcons.LightBulbIcon },
  cpuChip: { outline: OutlineIcons.CpuChipIcon, solid: SolidIcons.CpuChipIcon },

  // Misc
  eye: { outline: OutlineIcons.EyeIcon, solid: SolidIcons.EyeIcon },
  eyeSlash: { outline: OutlineIcons.EyeSlashIcon, solid: SolidIcons.EyeSlashIcon },
  link: { outline: OutlineIcons.LinkIcon, solid: SolidIcons.LinkIcon },
  share: { outline: OutlineIcons.ShareIcon, solid: SolidIcons.ShareIcon },
  clipboard: { outline: OutlineIcons.ClipboardIcon, solid: SolidIcons.ClipboardIcon },
  clipboardCheck: { outline: OutlineIcons.ClipboardDocumentCheckIcon, solid: SolidIcons.ClipboardDocumentCheckIcon },
  clipboardDocumentCheck: { outline: OutlineIcons.ClipboardDocumentCheckIcon, solid: SolidIcons.ClipboardDocumentCheckIcon },
  bookOpen: { outline: OutlineIcons.BookOpenIcon, solid: SolidIcons.BookOpenIcon },
  academicCap: { outline: OutlineIcons.AcademicCapIcon, solid: SolidIcons.AcademicCapIcon },
  buildingOffice: { outline: OutlineIcons.BuildingOfficeIcon, solid: SolidIcons.BuildingOfficeIcon },
  globe: { outline: OutlineIcons.GlobeAltIcon, solid: SolidIcons.GlobeAltIcon },
  photo: { outline: OutlineIcons.PhotoIcon, solid: SolidIcons.PhotoIcon },
  qrCode: { outline: OutlineIcons.QrCodeIcon, solid: SolidIcons.QrCodeIcon },
  arrowPath: { outline: OutlineIcons.ArrowPathIcon, solid: SolidIcons.ArrowPathIcon },
  ellipsisVertical: { outline: OutlineIcons.EllipsisVerticalIcon, solid: SolidIcons.EllipsisVerticalIcon },
  ellipsisHorizontal: { outline: OutlineIcons.EllipsisHorizontalIcon, solid: SolidIcons.EllipsisHorizontalIcon },
  funnel: { outline: OutlineIcons.FunnelIcon, solid: SolidIcons.FunnelIcon },
  bars3BottomLeft: { outline: OutlineIcons.Bars3BottomLeftIcon, solid: SolidIcons.Bars3BottomLeftIcon },
  listBullet: { outline: OutlineIcons.ListBulletIcon, solid: SolidIcons.ListBulletIcon },
  squares2x2: { outline: OutlineIcons.Squares2X2Icon, solid: SolidIcons.Squares2X2Icon },

  // Additional icons for Categories Manager
  scale: { outline: OutlineIcons.ScaleIcon, solid: SolidIcons.ScaleIcon },
  briefcase: { outline: OutlineIcons.BriefcaseIcon, solid: SolidIcons.BriefcaseIcon },
  star: { outline: OutlineIcons.StarIcon, solid: SolidIcons.StarIcon },
  archiveBox: { outline: OutlineIcons.ArchiveBoxIcon, solid: SolidIcons.ArchiveBoxIcon },
  mapPin: { outline: OutlineIcons.MapPinIcon, solid: SolidIcons.MapPinIcon },
  beaker: { outline: OutlineIcons.BeakerIcon, solid: SolidIcons.BeakerIcon },
  documentDuplicate: { outline: OutlineIcons.DocumentDuplicateIcon, solid: SolidIcons.DocumentDuplicateIcon },
  folderPlus: { outline: OutlineIcons.FolderPlusIcon, solid: SolidIcons.FolderPlusIcon },
  xMark: { outline: OutlineIcons.XMarkIcon, solid: SolidIcons.XMarkIcon },
  arrowTrendingUp: { outline: OutlineIcons.ArrowTrendingUpIcon, solid: SolidIcons.ArrowTrendingUpIcon },

  // Device icons
  devicePhoneMobile: { outline: OutlineIcons.DevicePhoneMobileIcon, solid: SolidIcons.DevicePhoneMobileIcon },
  deviceTablet: { outline: OutlineIcons.DeviceTabletIcon, solid: SolidIcons.DeviceTabletIcon },
  computerDesktop: { outline: OutlineIcons.ComputerDesktopIcon, solid: SolidIcons.ComputerDesktopIcon },

  // Additional utility icons
  sun: { outline: OutlineIcons.SunIcon, solid: SolidIcons.SunIcon },
  moon: { outline: OutlineIcons.MoonIcon, solid: SolidIcons.MoonIcon },
  printer: { outline: OutlineIcons.PrinterIcon, solid: SolidIcons.PrinterIcon },
  circleStack: { outline: OutlineIcons.CircleStackIcon, solid: SolidIcons.CircleStackIcon },
  pause: { outline: OutlineIcons.PauseIcon, solid: SolidIcons.PauseIcon },
  tag: { outline: OutlineIcons.TagIcon, solid: SolidIcons.TagIcon },

  // Icons used in AI and feedback features
  users: { outline: OutlineIcons.UserGroupIcon, solid: SolidIcons.UserGroupIcon },
  paperAirplane: { outline: OutlineIcons.PaperAirplaneIcon, solid: SolidIcons.PaperAirplaneIcon },
  handThumbUp: { outline: OutlineIcons.HandThumbUpIcon, solid: SolidIcons.HandThumbUpIcon },
  handThumbDown: { outline: OutlineIcons.HandThumbDownIcon, solid: SolidIcons.HandThumbDownIcon },
  x: { outline: OutlineIcons.XMarkIcon, solid: SolidIcons.XMarkIcon },
  arrowUp: { outline: OutlineIcons.ArrowUpIcon, solid: SolidIcons.ArrowUpIcon },
  arrowDown: { outline: OutlineIcons.ArrowDownIcon, solid: SolidIcons.ArrowDownIcon },

  // Additional subscription/billing icons
  rocket: { outline: OutlineIcons.RocketLaunchIcon, solid: SolidIcons.RocketLaunchIcon },
  gift: { outline: OutlineIcons.GiftIcon, solid: SolidIcons.GiftIcon },
  bolt: { outline: OutlineIcons.BoltIcon, solid: SolidIcons.BoltIcon },

  // Arrows and comparison icons
  arrowsRightLeft: { outline: OutlineIcons.ArrowsRightLeftIcon, solid: SolidIcons.ArrowsRightLeftIcon },
  arrowsUpDown: { outline: OutlineIcons.ArrowsUpDownIcon, solid: SolidIcons.ArrowsUpDownIcon },
  arrowTrendingDown: { outline: OutlineIcons.ArrowTrendingDownIcon, solid: SolidIcons.ArrowTrendingDownIcon },

  // Bookmark and chat
  bookmark: { outline: OutlineIcons.BookmarkIcon, solid: SolidIcons.BookmarkIcon },
  bookmarkSquare: { outline: OutlineIcons.BookmarkSquareIcon, solid: SolidIcons.BookmarkSquareIcon },
  chatBubbleLeftRight: { outline: OutlineIcons.ChatBubbleLeftRightIcon, solid: SolidIcons.ChatBubbleLeftRightIcon },
  chatBubbleOvalLeft: { outline: OutlineIcons.ChatBubbleOvalLeftIcon, solid: SolidIcons.ChatBubbleOvalLeftIcon },
  chatBubbleBottomCenterText: { outline: OutlineIcons.ChatBubbleBottomCenterTextIcon, solid: SolidIcons.ChatBubbleBottomCenterTextIcon },

  // Additional icons for glossary and search
  pencilSquare: { outline: OutlineIcons.PencilSquareIcon, solid: SolidIcons.PencilSquareIcon },
  magnifyingGlass: { outline: OutlineIcons.MagnifyingGlassIcon, solid: SolidIcons.MagnifyingGlassIcon },
  magnifyingGlassPlus: { outline: OutlineIcons.MagnifyingGlassPlusIcon, solid: SolidIcons.MagnifyingGlassPlusIcon },
  magnifyingGlassMinus: { outline: OutlineIcons.MagnifyingGlassMinusIcon, solid: SolidIcons.MagnifyingGlassMinusIcon },

  // Symbols and misc
  noSymbol: { outline: OutlineIcons.NoSymbolIcon, solid: SolidIcons.NoSymbolIcon },
  activity: { outline: OutlineIcons.ChartBarSquareIcon, solid: SolidIcons.ChartBarSquareIcon },
  play: { outline: OutlineIcons.PlayIcon, solid: SolidIcons.PlayIcon },
  stop: { outline: OutlineIcons.StopIcon, solid: SolidIcons.StopIcon },
  megaphone: { outline: OutlineIcons.MegaphoneIcon, solid: SolidIcons.MegaphoneIcon },

  // CRM icons
  clipboardDocumentList: { outline: OutlineIcons.ClipboardDocumentListIcon, solid: SolidIcons.ClipboardDocumentListIcon },
  magnifyingGlassChart: { outline: OutlineIcons.ChartBarSquareIcon, solid: SolidIcons.ChartBarSquareIcon }, // Using ChartBarSquare as fallback
  flag: { outline: OutlineIcons.FlagIcon, solid: SolidIcons.FlagIcon },
  videoCamera: { outline: OutlineIcons.VideoCameraIcon, solid: SolidIcons.VideoCameraIcon },
  arrowDownLeft: { outline: OutlineIcons.ArrowDownLeftIcon, solid: SolidIcons.ArrowDownLeftIcon },
  arrowUpRight: { outline: OutlineIcons.ArrowUpRightIcon, solid: SolidIcons.ArrowUpRightIcon },

  // Infrastructure/Server icons
  serverStack: { outline: OutlineIcons.ServerStackIcon, solid: SolidIcons.ServerStackIcon },
  server: { outline: OutlineIcons.ServerIcon, solid: SolidIcons.ServerIcon },
  globeAlt: { outline: OutlineIcons.GlobeAltIcon, solid: SolidIcons.GlobeAltIcon },
  arrowRightOnRectangle: { outline: OutlineIcons.ArrowRightOnRectangleIcon, solid: SolidIcons.ArrowRightOnRectangleIcon },
  arrowLeftOnRectangle: { outline: OutlineIcons.ArrowLeftOnRectangleIcon, solid: SolidIcons.ArrowLeftOnRectangleIcon },

  // User action icons
  userCheck: { outline: OutlineIcons.UserIcon, solid: SolidIcons.UserIcon }, // No direct equivalent, using User
  userMinus: { outline: OutlineIcons.UserMinusIcon, solid: SolidIcons.UserMinusIcon },
  envelopeCheck: { outline: OutlineIcons.EnvelopeIcon, solid: SolidIcons.EnvelopeIcon }, // No direct equivalent, using Envelope
  lifebuoy: { outline: OutlineIcons.LifebuoyIcon, solid: SolidIcons.LifebuoyIcon },

  // Aliases for kebab-case names (backward compatibility)
  mail: { outline: OutlineIcons.EnvelopeIcon, solid: SolidIcons.EnvelopeIcon },
  send: { outline: OutlineIcons.PaperAirplaneIcon, solid: SolidIcons.PaperAirplaneIcon },
  messageCircle: { outline: OutlineIcons.ChatBubbleOvalLeftIcon, solid: SolidIcons.ChatBubbleOvalLeftIcon },
  fileText: { outline: OutlineIcons.DocumentTextIcon, solid: SolidIcons.DocumentTextIcon },
  filePlus: { outline: OutlineIcons.DocumentPlusIcon, solid: SolidIcons.DocumentPlusIcon },
  alertTriangle: { outline: OutlineIcons.ExclamationTriangleIcon, solid: SolidIcons.ExclamationTriangleIcon },
  externalLink: { outline: OutlineIcons.ArrowTopRightOnSquareIcon, solid: SolidIcons.ArrowTopRightOnSquareIcon },
  trendingUp: { outline: OutlineIcons.ArrowTrendingUpIcon, solid: SolidIcons.ArrowTrendingUpIcon },
  zap: { outline: OutlineIcons.BoltIcon, solid: SolidIcons.BoltIcon },
  grid: { outline: OutlineIcons.Squares2X2Icon, solid: SolidIcons.Squares2X2Icon },
  list: { outline: OutlineIcons.ListBulletIcon, solid: SolidIcons.ListBulletIcon },
  buildingLibrary: { outline: OutlineIcons.BuildingLibraryIcon, solid: SolidIcons.BuildingLibraryIcon },
  documentPlus: { outline: OutlineIcons.DocumentPlusIcon, solid: SolidIcons.DocumentPlusIcon },
  arrowTopRightOnSquare: { outline: OutlineIcons.ArrowTopRightOnSquareIcon, solid: SolidIcons.ArrowTopRightOnSquareIcon },
  inbox: { outline: OutlineIcons.InboxIcon, solid: SolidIcons.InboxIcon },
  inboxArrowDown: { outline: OutlineIcons.InboxArrowDownIcon, solid: SolidIcons.InboxArrowDownIcon },
  envelopeOpen: { outline: OutlineIcons.EnvelopeOpenIcon, solid: SolidIcons.EnvelopeOpenIcon },
  handRaised: { outline: OutlineIcons.HandRaisedIcon, solid: SolidIcons.HandRaisedIcon },
  identification: { outline: OutlineIcons.IdentificationIcon, solid: SolidIcons.IdentificationIcon },
  minusCircle: { outline: OutlineIcons.MinusCircleIcon, solid: SolidIcons.MinusCircleIcon },
  plusCircle: { outline: OutlineIcons.PlusCircleIcon, solid: SolidIcons.PlusCircleIcon },
  arrowPathRoundedSquare: { outline: OutlineIcons.ArrowPathRoundedSquareIcon, solid: SolidIcons.ArrowPathRoundedSquareIcon },
  cursorArrowRays: { outline: OutlineIcons.CursorArrowRaysIcon, solid: SolidIcons.CursorArrowRaysIcon },
  documentCheck: { outline: OutlineIcons.DocumentCheckIcon, solid: SolidIcons.DocumentCheckIcon },
  documentMagnifyingGlass: { outline: OutlineIcons.DocumentMagnifyingGlassIcon, solid: SolidIcons.DocumentMagnifyingGlassIcon },
  faceSmile: { outline: OutlineIcons.FaceSmileIcon, solid: SolidIcons.FaceSmileIcon },
  faceFrown: { outline: OutlineIcons.FaceFrownIcon, solid: SolidIcons.FaceFrownIcon },
  calendarDateRange: { outline: OutlineIcons.CalendarDaysIcon, solid: SolidIcons.CalendarDaysIcon },
  atSymbol: { outline: OutlineIcons.AtSymbolIcon, solid: SolidIcons.AtSymbolIcon },
  hashtag: { outline: OutlineIcons.HashtagIcon, solid: SolidIcons.HashtagIcon },
  windowIcon: { outline: OutlineIcons.WindowIcon, solid: SolidIcons.WindowIcon },
  squaresPlus: { outline: OutlineIcons.SquaresPlusIcon, solid: SolidIcons.SquaresPlusIcon },
  viewColumns: { outline: OutlineIcons.ViewColumnsIcon, solid: SolidIcons.ViewColumnsIcon },
  language: { outline: OutlineIcons.LanguageIcon, solid: SolidIcons.LanguageIcon },
  currencyDollar: { outline: OutlineIcons.CurrencyDollarIcon, solid: SolidIcons.CurrencyDollarIcon },
  currencyPound: { outline: OutlineIcons.CurrencyPoundIcon, solid: SolidIcons.CurrencyPoundIcon },
  wallet: { outline: OutlineIcons.WalletIcon, solid: SolidIcons.WalletIcon },
  wrenchScrewdriver: { outline: OutlineIcons.WrenchScrewdriverIcon, solid: SolidIcons.WrenchScrewdriverIcon },
  adjustmentsHorizontal: { outline: OutlineIcons.AdjustmentsHorizontalIcon, solid: SolidIcons.AdjustmentsHorizontalIcon },
  adjustmentsVertical: { outline: OutlineIcons.AdjustmentsVerticalIcon, solid: SolidIcons.AdjustmentsVerticalIcon },
  sliders: { outline: OutlineIcons.AdjustmentsHorizontalIcon, solid: SolidIcons.AdjustmentsHorizontalIcon },
  bars2: { outline: OutlineIcons.Bars2Icon, solid: SolidIcons.Bars2Icon },
  bars3: { outline: OutlineIcons.Bars3Icon, solid: SolidIcons.Bars3Icon },
  bars4: { outline: OutlineIcons.Bars4Icon, solid: SolidIcons.Bars4Icon },
  power: { outline: OutlineIcons.PowerIcon, solid: SolidIcons.PowerIcon },
  signalSlash: { outline: OutlineIcons.SignalSlashIcon, solid: SolidIcons.SignalSlashIcon },
  signal: { outline: OutlineIcons.SignalIcon, solid: SolidIcons.SignalIcon },
  wifi: { outline: OutlineIcons.WifiIcon, solid: SolidIcons.WifiIcon },
  arrowLongLeft: { outline: OutlineIcons.ArrowLongLeftIcon, solid: SolidIcons.ArrowLongLeftIcon },
  arrowLongRight: { outline: OutlineIcons.ArrowLongRightIcon, solid: SolidIcons.ArrowLongRightIcon },
  arrowSmallDown: { outline: OutlineIcons.ArrowSmallDownIcon, solid: SolidIcons.ArrowSmallDownIcon },
  arrowSmallUp: { outline: OutlineIcons.ArrowSmallUpIcon, solid: SolidIcons.ArrowSmallUpIcon },
  arrowsPointingIn: { outline: OutlineIcons.ArrowsPointingInIcon, solid: SolidIcons.ArrowsPointingInIcon },
  arrowsPointingOut: { outline: OutlineIcons.ArrowsPointingOutIcon, solid: SolidIcons.ArrowsPointingOutIcon },
  musicalNote: { outline: OutlineIcons.MusicalNoteIcon, solid: SolidIcons.MusicalNoteIcon },
  film: { outline: OutlineIcons.FilmIcon, solid: SolidIcons.FilmIcon },
  microphone: { outline: OutlineIcons.MicrophoneIcon, solid: SolidIcons.MicrophoneIcon },
  speakerWave: { outline: OutlineIcons.SpeakerWaveIcon, solid: SolidIcons.SpeakerWaveIcon },
  speakerXMark: { outline: OutlineIcons.SpeakerXMarkIcon, solid: SolidIcons.SpeakerXMarkIcon },
  cube: { outline: OutlineIcons.CubeIcon, solid: SolidIcons.CubeIcon },
  cubeTransparent: { outline: OutlineIcons.CubeTransparentIcon, solid: SolidIcons.CubeTransparentIcon },
  puzzlePiece: { outline: OutlineIcons.PuzzlePieceIcon, solid: SolidIcons.PuzzlePieceIcon },
  variable: { outline: OutlineIcons.VariableIcon, solid: SolidIcons.VariableIcon },
  codeBracket: { outline: OutlineIcons.CodeBracketIcon, solid: SolidIcons.CodeBracketIcon },
  codeBracketSquare: { outline: OutlineIcons.CodeBracketSquareIcon, solid: SolidIcons.CodeBracketSquareIcon },
  commandLine: { outline: OutlineIcons.CommandLineIcon, solid: SolidIcons.CommandLineIcon },
  fire: { outline: OutlineIcons.FireIcon, solid: SolidIcons.FireIcon },
  trophy: { outline: OutlineIcons.TrophyIcon, solid: SolidIcons.TrophyIcon },
  cake: { outline: OutlineIcons.CakeIcon, solid: SolidIcons.CakeIcon },

  // Additional kebab-case aliases for backward compatibility
  checkBadge: { outline: OutlineIcons.CheckBadgeIcon, solid: SolidIcons.CheckBadgeIcon },
  checkCircleSolid: { outline: OutlineIcons.CheckCircleIcon, solid: SolidIcons.CheckCircleIcon },
  documentArrowDown: { outline: OutlineIcons.DocumentArrowDownIcon, solid: SolidIcons.DocumentArrowDownIcon },
  documentArrowUp: { outline: OutlineIcons.DocumentArrowUpIcon, solid: SolidIcons.DocumentArrowUpIcon },
  documentMinus: { outline: OutlineIcons.DocumentMinusIcon, solid: SolidIcons.DocumentMinusIcon },
  rocketLaunch: { outline: OutlineIcons.RocketLaunchIcon, solid: SolidIcons.RocketLaunchIcon },
  receiptPercent: { outline: OutlineIcons.ReceiptPercentIcon, solid: SolidIcons.ReceiptPercentIcon },
  buildingStorefront: { outline: OutlineIcons.BuildingStorefrontIcon, solid: SolidIcons.BuildingStorefrontIcon },
  buildingOffice2: { outline: OutlineIcons.BuildingOffice2Icon, solid: SolidIcons.BuildingOffice2Icon },
  homeModern: { outline: OutlineIcons.HomeModernIcon, solid: SolidIcons.HomeModernIcon },
  swatch: { outline: OutlineIcons.SwatchIcon, solid: SolidIcons.SwatchIcon },
  newspaper: { outline: OutlineIcons.NewspaperIcon, solid: SolidIcons.NewspaperIcon },
  ticketIcon: { outline: OutlineIcons.TicketIcon, solid: SolidIcons.TicketIcon },
  ticket: { outline: OutlineIcons.TicketIcon, solid: SolidIcons.TicketIcon },
  calculator: { outline: OutlineIcons.CalculatorIcon, solid: SolidIcons.CalculatorIcon },
  presentationChartBar: { outline: OutlineIcons.PresentationChartBarIcon, solid: SolidIcons.PresentationChartBarIcon },
  presentationChartLine: { outline: OutlineIcons.PresentationChartLineIcon, solid: SolidIcons.PresentationChartLineIcon },
  chartBarSquare: { outline: OutlineIcons.ChartBarSquareIcon, solid: SolidIcons.ChartBarSquareIcon },
  clipboardDocument: { outline: OutlineIcons.ClipboardDocumentIcon, solid: SolidIcons.ClipboardDocumentIcon },
  bars3CenterLeft: { outline: OutlineIcons.Bars3CenterLeftIcon, solid: SolidIcons.Bars3CenterLeftIcon },
  exclamation: { outline: OutlineIcons.ExclamationCircleIcon, solid: SolidIcons.ExclamationCircleIcon },
  inboxStack: { outline: OutlineIcons.InboxStackIcon, solid: SolidIcons.InboxStackIcon },
  giftTop: { outline: OutlineIcons.GiftTopIcon, solid: SolidIcons.GiftTopIcon },
  squares2X2: { outline: OutlineIcons.Squares2X2Icon, solid: SolidIcons.Squares2X2Icon },

  // CRM/Advisor icons
  chatBubbleLeft: { outline: OutlineIcons.ChatBubbleLeftIcon, solid: SolidIcons.ChatBubbleLeftIcon },
} as const;

export type IconName = keyof typeof iconMap;
export type IconVariant = 'outline' | 'solid';

export interface IconProps extends ComponentProps<'svg'> {
  /** Icon name from the predefined map */
  name: IconName;
  /** Icon variant: outline (24px, thin) or solid (20px, filled) */
  variant?: IconVariant;
  /** Size shorthand - applies both width and height */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
  xl: 'h-8 w-8',
};

/**
 * Icon component with predefined icon names mapped to Heroicons.
 *
 * For icons not in the map, import directly from @heroicons/react:
 * ```tsx
 * import { DocumentIcon } from '@heroicons/react/24/outline';
 * ```
 */
export function Icon({ name, variant = 'outline', size = 'md', className = '', ...props }: IconProps) {
  const iconConfig = iconMap[name];
  if (!iconConfig) {
    console.warn(`Icon "${name}" not found in icon map`);
    return null;
  }

  const IconComponent = iconConfig[variant];
  const sizeClass = sizeClasses[size];

  return <IconComponent className={`${sizeClass} ${className}`.trim()} {...props} />;
}

// Re-export Heroicons for direct use
export { OutlineIcons, SolidIcons };
